import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { stageLabel } from "../lib/constants";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { SquaresFour, Users, Kanban, Files, CheckSquare, ClockCounterClockwise } from "@phosphor-icons/react";

const pages = [
  { label: "Dashboard", path: "/", icon: SquaresFour },
  { label: "Clients", path: "/clients", icon: Users },
  { label: "Pipeline", path: "/pipeline", icon: Kanban },
  { label: "Documents", path: "/documents", icon: Files },
  { label: "Approval Queue", path: "/approvals", icon: CheckSquare },
  { label: "Audit Log", path: "/audit", icon: ClockCounterClockwise },
];

export function CommandPalette({ open, setOpen }) {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["search"], queryFn: () => api.get("/search").then((r) => r.data), enabled: open });

  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setOpen]);

  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search clients, engagements, pages..." data-testid="command-palette-input" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Pages">
          {pages.map((p) => (
            <CommandItem key={p.path} onSelect={() => go(p.path)} data-testid={`cmd-page-${p.label.toLowerCase().replace(/ /g, "-")}`}>
              <p.icon size={16} className="mr-2" /> {p.label}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Clients">
          {(data?.clients || []).map((c) => (
            <CommandItem key={c.id} onSelect={() => go(`/clients/${c.id}`)}>
              <Users size={16} className="mr-2" /> {c.name} <span className="ml-2 text-xs text-zinc-400">{c.entity_type}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Engagements">
          {(data?.engagements || []).map((e) => (
            <CommandItem key={e.id} onSelect={() => go("/pipeline")}>
              <Kanban size={16} className="mr-2" /> {e.client_name} — {e.service_type} {e.tax_year}
              <span className="ml-2 text-xs text-zinc-400">{stageLabel(e.stage)}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

export function usePalette() {
  const [open, setOpen] = useState(false);
  return { open, setOpen };
}
